/**
 * English (en-US) source strings.
 *
 * Copy in ChorePop is deliberately warm, second-person, and gentle —
 * closer to "Will post to Freya's buckets" than "Deposit amount". The
 * design prototype's literals are the canonical en-US values.
 *
 * The `strings` export below comes verbatim from the design handoff
 * (handoff/packages/core/src/i18n/en-US.ts). Keep keys stable; add new
 * locales by mirroring this file's shape.
 *
 * The `messages` export is a legacy shape from the pre-handoff codebase.
 * It is preserved as a re-export bridge so older imports keep working
 * while screens migrate to the canonical `strings` shape.
 */

export const strings = {
  brand: {
    name: 'ChorePop',
    tagline: 'Chores pop. Confidence grows.',
  },
  picker: {
    title: 'Pick your profile',
    parents: 'Parents & Guardians',
    kids: 'Kids',
    addChild: '+ Add a child',
    settings: 'Settings',
    backup: 'Backup & restore',
    sync: 'Cloud-synced · works offline · protected family space',
  },
  guardianHome: {
    heading: (n: number) => (n === 1 ? '1 thing needs your eyes' : `${n} things need your eyes`),
    filters: {
      all: 'All',
      chores: 'Chores',
      deposits: 'Deposits',
      withdrawals: 'Withdrawals',
    },
    signedInAs: (name: string) => `Signed in as ${name}`,
  },
  approval: {
    willPostCaption: (name: string) => `Will post to ${name}'s buckets`,
    approve: 'Approve',
    reject: 'Reject',
    rejectPrompt: "What should we tell them? (they'll see this)",
    amount: 'Amount',
    source: 'Source',
    split: 'Split preview',
  },
  child: {
    totalLabel: 'Your total · all buckets',
    totalCaption: (guardians: string) => `held by ${guardians} in real cash`,
    legend: 'Your split — every £1 that comes in goes…',
    logChore: 'Log a chore',
    logDeposit: 'Log a deposit',
    withdraw: 'Withdraw',
    waiting: 'Waiting for parents',
    recent: 'Recent',
    rules: 'Your bucket rules',
    editBuckets: 'Edit my buckets',
    todaysChores: "Today's chores",
  },
  buckets: {
    allocation: 'Allocation',
    mustSumTo100: 'Must sum to 100%',
    sumsTo100: 'Sums to 100%',
    addBucket: '+ Add a new bucket',
    preview: 'Preview',
    currentBalances: 'Current balances',
    rules: {
      approvalToWithdraw: 'Approval to withdraw',
      interestCredited: 'Interest credited',
      parentManaged: 'Parent-managed',
    },
  },
  history: {
    title: 'Family history',
    subtitle: 'Immutable audit log · REQ-11',
    filters: {
      all: 'Everything',
      deposits: 'Deposits',
      withdrawals: 'Withdrawals',
      chores: 'Chores',
      interest: 'Interest',
      bucketChanges: 'Bucket changes',
    },
    rejectedBadge: 'Rejected',
  },
} as const;

/**
 * Legacy strings shape preserved for backwards compatibility.
 * @deprecated New screens should consume `strings` (canonical handoff
 * shape). This object will be removed once all screens are migrated.
 */
export const messages = {
  app: { name: strings.brand.name },
  auth: {
    loginTitle: 'Who are you?',
    passwordPrompt: 'Enter password',
    lockedOut: 'Too many attempts — try again in a moment.',
  },
  buckets: {
    parentManagedLabel: strings.buckets.rules.parentManaged,
    childManagedLabel: 'My bucket',
  },
  deposits: {
    pendingTitle: 'Waiting for approval',
    approvedToast: 'Deposit approved and added to your buckets.',
  },
  withdrawals: { reasonRequired: 'Please give a reason for this spend.' },
} as const;

export type Messages = typeof messages;
